import React from "react";
import { Box, IconButton, Typography, useTheme, SxProps, Theme } from "@mui/material";
import { Control, useWatch, Controller } from "react-hook-form";
import { tokens } from "../../theme";
import CloseIcon from "@mui/icons-material/Close";
import { TextField as MUITextField } from "@mui/material";
import { useModalStyles } from "./useModalStyles";

interface CustomInvoiceAgingProps {
  label?: string;
  control: Control<any, any>;
  minControllerName: string;
  maxControllerName: string;
  minLabel?: string;
  maxLabel?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  disabled?: boolean;
  /** When true, use outlined fields + label row to match modal dropdown/text inputs (e.g. Share Invoice). */
  outlinedModal?: boolean;
  minRules?: object;
  maxRules?: object;
  autoFocusMin?: boolean;
}

const CustomInvoiceAging: React.FC<CustomInvoiceAgingProps> = ({
  label,
  control,
  minControllerName,
  maxControllerName,
  minLabel = "Min Days",
  maxLabel = "Max Days",
  minPlaceholder,
  maxPlaceholder,
  disabled = false,
  outlinedModal = false,
  minRules,
  maxRules,
  autoFocusMin = false,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { tokens: mt } = useModalStyles();
  const minValue = useWatch({ control, name: minControllerName });
  const maxValue = useWatch({ control, name: maxControllerName });
  const hasValues = minValue || maxValue;

  const handleClear = () => {
    control._reset({
      ...control._formValues,
      [minControllerName]: "",
      [maxControllerName]: "",
    });
  };

  const modalOutlineFieldSx: SxProps<Theme> = {
    "& .MuiOutlinedInput-root": {
      minHeight: 35,
      fontSize: "0.8125rem",
      borderRadius: "10px",
    },
    "& .MuiInputBase-input": { py: 0.75 },
    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: `${theme.palette.error.main} !important`,
    },
    "& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${theme.palette.error.main} !important`,
    },
    "& .MuiInputLabel-root.Mui-error": {
      color: `${theme.palette.error.main} !important`,
    },
    "& .MuiFormHelperText-root": {
      marginTop: "0px",
      marginBottom: "8px",
      lineHeight: 1.2,
    },
  };

  const legacyFieldSx: SxProps<Theme> = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "" : colors.primary[100],
      },
      "&.Mui-disabled": {
        "& fieldset": {
          borderColor: disabled && theme.palette.mode === "light" ? colors.grey[300] : "",
        },
        "& input": {
          color: disabled && theme.palette.mode === "light" ? colors.grey[300] : "",
        },
      },
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: disabled && theme.palette.mode === "light" ? colors.grey[300] : "",
    },
    "& .MuiInputLabel-outlined": {
      color: theme.palette.mode === "dark" ? "" : colors.primary[100],
      "&.Mui-disabled": {
        color: disabled && theme.palette.mode === "light" ? colors.grey[300] : "",
      },
    },
  };

  if (outlinedModal) {
    return (
      <Box sx={{ width: "100%" }}>
        {label && (
          <Typography
            component="label"
            sx={{
              display: "block",
              fontSize: "0.75rem",
              fontWeight: 500,
              color: mt.textSecondary,
              mb: 0.75,
            }}
          >
            {label}
          </Typography>
        )}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            // CS-2028 - top-align so the validation messages flow below the inputs without the "-" overlapping them
            alignItems: "flex-start",
            gap: 1,
            pr: hasValues && !disabled ? 4 : 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Controller
              name={minControllerName}
              control={control}
              rules={minRules}
              render={({ field, fieldState }) => (
                <MUITextField
                  id={field.name}
                  {...field}
                  value={field.value || ""}
                  name={minControllerName}
                  label={minLabel}
                  placeholder={minPlaceholder}
                  fullWidth
                  autoFocus={autoFocusMin}
                  type="text"
                  onChange={(e: any) => {
                    let value = e.target.value;
                    value = value.replace(/[^0-9-]/g, "");
                    if (value.indexOf("-") > 0) {
                      value = value.replace(/-/g, "");
                    }
                    const minusCount = (value.match(/-/g) || []).length;
                    if (minusCount > 1) {
                      value = "-" + value.replace(/-/g, "");
                    }
                    field.onChange(value);
                  }}
                  onBlur={(e: any) => {
                    let value = e.target.value?.trim();
                    if (value === "-") {
                      value = "";
                    }
                    field.onChange(value);
                    field.onBlur();
                  }}
                  size="small"
                  color={theme.palette.mode === "dark" ? "secondary" : "primary"}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: disabled ? true : undefined, // CS-2028 - let the label float up on focus (MUI auto), matching other fields
                  }}
                  error={!!fieldState.error}
                  helperText={fieldState.error ? fieldState.error.message : null}
                  disabled={disabled}
                  sx={modalOutlineFieldSx}
                />
              )}
            />
          </Box>
          <Typography
            sx={{
              color: mt.textSecondary,
              fontSize: "0.875rem",
              flexShrink: 0,
              // CS-2028 - match the input height and center the dash so it lines up with the fields (not the message)
              height: 35,
              display: "flex",
              alignItems: "center",
            }}
          >
            -
          </Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Controller
              name={maxControllerName}
              control={control}
              rules={maxRules}
              render={({ field, fieldState }) => (
                <MUITextField
                  id={field.name}
                  {...field}
                  value={field.value || ""}
                  name={maxControllerName}
                  label={maxLabel}
                  placeholder={maxPlaceholder}
                  fullWidth
                  type="text"
                  onChange={(e: any) => {
                    let value = e.target.value;
                    value = value.replace(/[^0-9-]/g, "");
                    if (value.indexOf("-") > 0) {
                      value = value.replace(/-/g, "");
                    }
                    const minusCount = (value.match(/-/g) || []).length;
                    if (minusCount > 1) {
                      value = "-" + value.replace(/-/g, "");
                    }
                    field.onChange(value);
                  }}
                  onBlur={(e: any) => {
                    let value = e.target.value?.trim();
                    if (value === "-") {
                      value = "";
                    }
                    field.onChange(value);
                    field.onBlur();
                  }}
                  size="small"
                  color={theme.palette.mode === "dark" ? "secondary" : "primary"}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: disabled ? true : undefined, // CS-2028 - let the label float up on focus (MUI auto), matching other fields
                  }}
                  error={!!fieldState.error}
                  helperText={fieldState.error ? fieldState.error.message : null}
                  disabled={disabled}
                  sx={modalOutlineFieldSx}
                />
              )}
            />
          </Box>
          {hasValues && !disabled && (
            <IconButton
              onClick={handleClear}
              aria-label="Clear age range"
              sx={{
                position: "absolute",
                right: 0,
                top: "50%",
                transform: "translateY(-50%)",
                p: 0.5,
                color: mt.textSecondary,
                "&:hover": { color: mt.textPrimary, bgcolor: "transparent" },
              }}
              size="small"
            >
              <CloseIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        border: `1px solid ${
          theme.palette.mode === "dark" ? colors.grey[700] : colors.primary[100]
        }`,
        borderRadius: "4px",
        padding: "5px 8px",
        paddingRight: hasValues ? "32px" : "8px",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        backgroundColor: "transparent",
        "&:hover": {
          borderColor: theme.palette.mode === "dark" ? colors.grey[100] : colors.primary[500],
        },
      }}
    >
      {label && (
        <Box
          component="label"
          sx={{
            position: "absolute",
            top: 0,
            left: "8px",
            transform: "translateY(-50%)",
            backgroundColor: theme.palette.mode === "dark" ? "#171e26" : "#f9f6f6",
            padding: "0 4px",
            fontSize: "11px",
            color: theme.palette.mode === "dark" ? colors.grey[400] : colors.primary[100],
          }}
        >
          {label}
        </Box>
      )}
      {hasValues && !disabled && (
        <IconButton
          onClick={handleClear}
          sx={{
            position: "absolute",
            top: "50%",
            right: "4px",
            transform: "translateY(-50%)",
            padding: "4px",
            backgroundColor: theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[700],
            "&:hover": {
              backgroundColor: theme.palette.mode === "dark" ? colors.grey[700] : colors.grey[600],
            },
          }}
          size="small"
        >
          <CloseIcon sx={{ fontSize: "14px" }} />
        </IconButton>
      )}
      <Box sx={{ flex: 1 }}>
        <Controller
          name={minControllerName}
          control={control}
          rules={minRules}
          render={({ field, fieldState }) => (
            <MUITextField
              id={field.name}
              {...field}
              value={field.value || ""}
              name={minControllerName}
              label={minLabel}
              placeholder={minPlaceholder}
              fullWidth
              autoFocus={autoFocusMin}
              type="text"
              onChange={(e: any) => {
                let value = e.target.value;
                value = value.replace(/[^0-9-]/g, "");
                if (value.indexOf("-") > 0) {
                  value = value.replace(/-/g, "");
                }
                const minusCount = (value.match(/-/g) || []).length;
                if (minusCount > 1) {
                  value = "-" + value.replace(/-/g, "");
                }
                field.onChange(value);
              }}
              onBlur={(e: any) => {
                let value = e.target.value?.trim();
                if (value === "-") {
                  value = "";
                }
                field.onChange(value);
                field.onBlur();
              }}
              size="small"
              color={theme.palette.mode === "dark" ? "secondary" : "primary"}
              variant="standard"
              InputLabelProps={{
                shrink: disabled ? true : Boolean(field.value),
              }}
              error={!!fieldState.error}
              helperText={fieldState.error ? fieldState.error.message : null}
              disabled={disabled}
              sx={legacyFieldSx}
            />
          )}
        />
      </Box>
      <Box
        sx={{
          color: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[500],
          fontSize: "14px",
        }}
      >
        -
      </Box>
      <Box sx={{ flex: 1 }}>
        <Controller
          name={maxControllerName}
          control={control}
          rules={maxRules}
          render={({ field, fieldState }) => (
            <MUITextField
              id={field.name}
              {...field}
              value={field.value || ""}
              name={maxControllerName}
              label={maxLabel}
              placeholder={maxPlaceholder}
              fullWidth
              type="text"
              onChange={(e: any) => {
                let value = e.target.value;
                value = value.replace(/[^0-9-]/g, "");
                if (value.indexOf("-") > 0) {
                  value = value.replace(/-/g, "");
                }
                const minusCount = (value.match(/-/g) || []).length;
                if (minusCount > 1) {
                  value = "-" + value.replace(/-/g, "");
                }
                field.onChange(value);
              }}
              onBlur={(e: any) => {
                let value = e.target.value?.trim();
                if (value === "-") {
                  value = "";
                }
                field.onChange(value);
                field.onBlur();
              }}
              size="small"
              color={theme.palette.mode === "dark" ? "secondary" : "primary"}
              variant="standard"
              InputLabelProps={{
                shrink: disabled ? true : Boolean(field.value),
              }}
              error={!!fieldState.error}
              helperText={fieldState.error ? fieldState.error.message : null}
              disabled={disabled}
              sx={legacyFieldSx}
            />
          )}
        />
      </Box>
    </Box>
  );
};

export default CustomInvoiceAging;
